import { Component } from '@theme/component';
import { supportsViewTransitions, startViewTransition } from '@theme/utilities';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * PhotoSwipe Gallery Component
 * 使用PhotoSwipe替代原有的zoom-dialog实现大图预览功能
 */
export class PhotoSwipeGallery extends Component {
  requiredRefs = ['gallery'];

  #lightbox = null;
  #items = [];
  #currentIndex = 0;

  connectedCallback() {
    super.connectedCallback();
    // 立即初始化 - 先确保功能正常
    this.waitForPhotoSwipeAndInitialize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#lightbox && typeof this.#lightbox.destroy === 'function') {
      this.#lightbox.destroy();
      this.#lightbox = null;
    }
  }

  /**
   * 等待PhotoSwipe加载完成然后初始化
   */
  waitForPhotoSwipeAndInitialize() {
    // 检查PhotoSwipe是否已经加载
    if (window.PhotoSwipe && window.PhotoSwipeLightbox) {
      this.initializePhotoSwipe();
      return;
    }

    // 等待PhotoSwipe加载完成
    window.addEventListener('photoswipe:loaded', () => {
      this.initializePhotoSwipe();
    });

  }


  /**
   * 初始化PhotoSwipe
   */
  initializePhotoSwipe() {
    try {
      // 检查PhotoSwipe库是否已加载
      if (typeof window.PhotoSwipeLightbox === 'undefined') {
        return;
      }

      if (typeof window.PhotoSwipe === 'undefined') {
        return;
      }

      // 准备图片数据
      this.prepareGalleryItems();

      // 创建PhotoSwipe Lightbox实例 - 优化配置改善加载体验
      this.#lightbox = new window.PhotoSwipeLightbox({
        dataSource: this.#items,
        pswpModule: window.PhotoSwipe,
        bgOpacity: 1,
        close: true,
        counter: false, //是否显示计数
        escKey: true, //按 ESC 键关闭弹窗
        zoom: true,
        maxSpreadZoom: 3, //最大缩放比例
        initialZoomLevel: 1, //初始缩放级别
        secondaryZoomLevel: 2, //双击缩放级别
        maxZoomLevel: 2, //最大缩放级别

        // 触摸和拖拽配置
        allowMouseDrag: true, //允许用鼠标拖拽图片（PC 端）
        allowTouchSwipe: true, //允许用手指拖拽图片（移动端）
        allowPanToNext: true, //允许在图片放大时左右滑动直接切到下一张
        // clickToCloseNonZoomable: true, //点击非可缩放图片时关闭弹窗

        paddingFn: (viewportSize) => {
          return {
            top: 30,
            bottom: 100
          };
        }
      });


      // ----------------- Lightbox 关闭事件 -----------------
      if (this.#lightbox) {
        this.#lightbox.on('close', () => {
          try {


            const gallery = this.closest('media-gallery');
            const slideshow = gallery?.querySelector('slideshow-component') ?? document.querySelector('slideshow-component');
            if (!slideshow) return;

            const idx = Number.isFinite(this.#currentIndex)
              ? this.#currentIndex
              : (this.#lightbox?.pswp?.currIndex ?? 0);

            // 1️⃣ 高亮选中当前图片
            if (typeof slideshow.select === 'function') slideshow.select(idx, undefined, { animate: false });

            // 2️⃣ 找到缩略图容器
            const desktopThumb = slideshow.refs?.thumbnails?.[idx];
            const container = desktopThumb?.closest('.slideshow-controls__thumbnails-container');

            const mobileRoot = slideshow.querySelector('.media-gallery__mobile-controls') || slideshow.refs?.mobileSlideshowControls;
            const mobileContainer = mobileRoot?.querySelector('.slideshow-controls__thumbnails-container');

            // 3️⃣ 居中缩略图
            const centerThumbnails = () => {
              // PC
              if (container && desktopThumb) {
                const containerRect = container.getBoundingClientRect();
                const thumbRect = desktopThumb.getBoundingClientRect();
                container.scrollTop =
                  container.scrollTop +
                  (thumbRect.top - containerRect.top - container.clientHeight / 2 + thumbRect.height / 2);
              }

              // Mobile
              if (mobileContainer) {
                const thumb = mobileContainer.querySelectorAll('.slideshow-controls__thumbnail')[idx];
                if (thumb) {
                  mobileContainer.scrollLeft =
                    thumb.offsetLeft - (mobileContainer.clientWidth - thumb.offsetWidth) / 2;
                }
              }
            };

            // 4️⃣ 使用 requestAnimationFrame 双保险，覆盖浏览器原生回滚
            let attempts = 0;
            const maxAttempts = 30;

            const step = () => {
              // 🔒 锁定容器滚动
              if (container) container.style.overflow = 'hidden';
              if (mobileContainer) mobileContainer.style.overflow = 'hidden';

              centerThumbnails();
              attempts++;

              if (attempts < maxAttempts) {
                requestAnimationFrame(step);
              } else {
                // 🔓 恢复容器滚动
                if (container) container.style.overflow = '';
                if (mobileContainer) mobileContainer.style.overflow = '';
              }
            };

            step();
          } catch (err) {
            console.warn('Thumbnail restore error', err);
          }
        });
      }

      // 添加过滤器来处理图片元素
      this.#lightbox.addFilter('thumbEl', (thumbEl, data, index) => {
        const slide = this.closest('media-gallery').querySelectorAll('slideshow-slide')[index];
        if (slide) {
          const img = slide.querySelector('img.product-media__image');
          if (img) {
            return img;
          }
        }
        return thumbEl;
      });

      this.#lightbox.addFilter('placeholderSrc', (placeholderSrc, slide) => {
        const slideEl = this.closest('media-gallery').querySelectorAll('slideshow-slide')[slide.index];
        if (slideEl) {
          const img = slideEl.querySelector('img.product-media__image');
          if (img) {
            return img.src;
          }
        }
        return placeholderSrc;
      });

      // 设置事件监听器
      this.setupPhotoSwipeEvents();

      // 创建自定义底部导航按钮
      this.createCustomBottomNav();

      // 预加载第一张图片以减少加载时间
      this.preloadFirstImage();

      // 延迟初始化，确保PhotoSwipe完全加载
      this.#lightbox.init();

      // 手动绑定点击事件到所有slides
      this.bindClickEvents();

      window.pswpLightbox = this.#lightbox;
    } catch (error) {
      // Failed to initialize PhotoSwipe
    }
  }

  /**
   * 准备画廊图片数据
   */
  prepareGalleryItems() {
    // 从父级元素查找slideshow-slide，因为photoswipe-gallery本身不包含这些元素
    const parentGallery = this.closest('media-gallery');
    if (!parentGallery) {
      return;
    }

    const slides = parentGallery.querySelectorAll('slideshow-slide');

    // 过滤出可缩放的图片
    this.#items = Array.from(slides).map((slide, index) => {
      const img = slide.querySelector('img.product-media__image');
      if (!img) return null;

      // 确保图片数据格式正确 - 使用官方示例的格式
      let src = img.getAttribute('data_max_resolution') || img.src;
      // 确保URL包含协议
      if (src && src.startsWith('//')) {
        src = 'https:' + src;
      }

      // 获取图片的实际尺寸 - 这对于Zoom动画至关重要
      const naturalWidth = img.naturalWidth || 1200;
      const naturalHeight = img.naturalHeight || 900;

      const item = {
        src: src,
        w: naturalWidth, // 使用w属性（PhotoSwipe标准）
        h: naturalHeight, // 使用h属性（PhotoSwipe标准）
        alt: img.getAttribute('alt') || '',
        title: img.getAttribute('alt') || '' // 添加title属性
      };

      return item;
    }).filter(item => item !== null);
  }

  /**
   * 预加载第一张图片以减少加载时间
   * 优化：在后台预加载所有图片，使用 requestIdleCallback 避免阻塞主线程
   */
  preloadFirstImage() {
    if (!this.#items.length) return;

    // 使用 requestIdleCallback 在空闲时预加载，如果浏览器不支持则使用 setTimeout
    const schedulePreload = (callback) => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(callback, { timeout: 2000 });
      } else {
        setTimeout(callback, 100);
      }
    };

    // 立即预加载第一张图片（最重要，用户最可能点击）
    if (this.#items[0] && this.#items[0].src) {
      const firstImg = new Image();
      firstImg.src = this.#items[0].src;
    }

    // 在后台预加载其余图片，避免阻塞主线程
    if (this.#items.length > 1) {
      let currentIndex = 1;
      
      const preloadNext = () => {
        if (currentIndex >= this.#items.length) return;

        const item = this.#items[currentIndex];
        if (item && item.src) {
          // 预加载图片
          const img = new Image();
          img.src = item.src;
          currentIndex++;
          
          // 继续预加载下一张（在空闲时）
          schedulePreload(preloadNext);
        } else {
          currentIndex++;
          schedulePreload(preloadNext);
        }
      };

      // 开始预加载第二张及之后的图片
      schedulePreload(preloadNext);
    }
  }

  /**
   * 设置PhotoSwipe事件监听器
   */
  setupPhotoSwipeEvents() {
    if (!this.#lightbox) return;

    // 打开事件
    this.#lightbox.on('open', () => {
      this.onPhotoSwipeOpen();
    });

    // 关闭事件
    this.#lightbox.on('close', () => {
      this.onPhotoSwipeClose();
    });

    // 变化事件
    this.#lightbox.on('change', () => {
      this.onPhotoSwipeChange();
    });

    // 键盘事件
    this.#lightbox.on('keydown', (e) => {
      this.handleKeyDown(e);
    });
  }

  /**
   * PhotoSwipe打开事件
   */
  onPhotoSwipeOpen() {
    document.body.classList.add('photoswipe-open');
    this.#currentIndex = this.#lightbox.pswp.currIndex;

    // 同步外部产品图片轮播到当前索引
    this.syncExternalSlideshow(this.#currentIndex);

    // 触发自定义事件
    window.dispatchEvent(new CustomEvent('photoswipe:open', {
      detail: { index: this.#currentIndex }
    }));
  }

  /**
   * PhotoSwipe关闭事件
   */
  onPhotoSwipeClose() {
    document.body.classList.remove('photoswipe-open');

    // 确保外部轮播保持当前状态
    if (this.#currentIndex >= 0) {
      this.syncExternalSlideshow(this.#currentIndex);
    }

    // 触发自定义事件
    window.dispatchEvent(new CustomEvent('photoswipe:close', {
      detail: { index: this.#currentIndex }
    }));

    // 触发DialogCloseEvent以保持兼容性
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * PhotoSwipe变化事件
   */
  onPhotoSwipeChange() {
    this.#currentIndex = this.#lightbox.pswp.currIndex;

    // 同步外部产品图片轮播
    this.syncExternalSlideshow(this.#currentIndex);

    // 触发自定义事件
    window.dispatchEvent(new CustomEvent('photoswipe:change', {
      detail: { index: this.#currentIndex }
    }));
  }

  /**
   * 同步外部产品图片轮播
   * @param {number} index - 当前图片索引
   */
  syncExternalSlideshow(index) {
    try {
      // 防止无效索引
      if (index < 0 || index >= this.#items.length) {
        return;
      }

      // 查找外部的slideshow组件
      const mediaGallery = this.closest('media-gallery');
      if (!mediaGallery) return;

      // 使用正确的标签名 slideshow-component
      const slideshow = mediaGallery.querySelector('slideshow-component');
      if (!slideshow) return;

      // 检查slideshow是否有select方法
      if (typeof slideshow.select !== 'function') {
        return;
      }

      // 检查当前slideshow是否已经显示正确的图片
      const currentSlide = slideshow.querySelector(`slideshow-slide:nth-child(${index + 1})`);
      if (currentSlide && currentSlide.getAttribute('aria-hidden') === 'false') {
        // 已经显示正确的图片，无需切换
        return;
      }

      // 调用slideshow的select方法来切换图片
      slideshow.select(index, undefined, { animate: false });
    } catch (error) {
      console.warn('Failed to sync external slideshow:', error);
    }
  }

  /**
   * 处理键盘事件
   */
  handleKeyDown(event) {
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        if (this.#lightbox && this.#lightbox.pswp) {
          this.#lightbox.pswp.close();
        }
        break;
      case 'ArrowLeft':
        event.preventDefault();
        if (this.#lightbox && this.#lightbox.pswp) {
          this.#lightbox.pswp.prev();
          // 键盘导航后同步外部轮播
          setTimeout(() => {
            if (this.#lightbox && this.#lightbox.pswp) {
              this.syncExternalSlideshow(this.#lightbox.pswp.currIndex);
            }
          }, 100);
        }
        break;
      case 'ArrowRight':
        event.preventDefault();
        if (this.#lightbox && this.#lightbox.pswp) {
          this.#lightbox.pswp.next();
          // 键盘导航后同步外部轮播
          setTimeout(() => {
            if (this.#lightbox && this.#lightbox.pswp) {
              this.syncExternalSlideshow(this.#lightbox.pswp.currIndex);
            }
          }, 100);
        }
        break;
    }
  }

  /**
   * 打开指定索引的图片
   * @param {number} index - 图片索引
   * @param {Event} event - 触发事件
   */
  open(index, event) {
    event.preventDefault();

    if (!this.#lightbox) {
      console.error('PhotoSwipe not initialized');
      return;
    }

    // 支持视图过渡
    if (supportsViewTransitions && event.target) {
      const sourceElement = event.target.closest('slideshow-slide');
      if (sourceElement) {
        this.openWithTransition(index, sourceElement);
        return;
      }
    }

    // 直接打开
    this.#lightbox.loadAndOpen(index);
  }

  /**
   * 使用视图过渡打开图片
   * @param {number} index - 图片索引
   * @param {HTMLElement} sourceElement - 源元素
   */
  async openWithTransition(index, sourceElement) {
    const transitionName = 'gallery-item';
    sourceElement.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      sourceElement.style.removeProperty('view-transition-name');
      this.#lightbox.loadAndOpen(index);
    });
  }

  /**
   * 关闭PhotoSwipe
   */
  close() {
    if (this.#lightbox && this.#lightbox.pswp) {
      this.#lightbox.pswp.close();
    }
  }

  /**
   * 获取当前索引
   */
  get currentIndex() {
    return this.#currentIndex;
  }

  /**
   * 获取总图片数
   */
  get totalImages() {
    return this.#items.length;
  }

  /**
 * 打开PhotoSwipe
 */
  openPhotoSwipe(index) {
    if (!this.#lightbox) {
      return;
    }

    try {
      // 直接尝试打开，让PhotoSwipe自己处理实例创建
      this.#lightbox.loadAndOpen(index);
    } catch (error) {
      // Failed to open PhotoSwipe
    }
  }



  /**
   * 自定义PhotoSwipe原有按钮样式
   */
  createCustomBottomNav() {
    if (!this.#lightbox) return;

    // 监听PhotoSwipe UI注册事件
    this.#lightbox.on('uiRegister', () => {
      // 等待PhotoSwipe完全初始化
      this.#lightbox.on('firstUpdate', () => {
        this.customizeExistingButtons();
      });
    });
  }

  /**
   * 自定义PhotoSwipe原有的按钮样式
   */
  customizeExistingButtons() {
    const pswpElement = this.#lightbox.pswp.element;
    if (!pswpElement) return;

    // 查找并自定义上一张按钮
    const prevBtn = pswpElement.querySelector('.pswp__button--arrow--prev');
    if (prevBtn) {
      prevBtn.classList.add('pswp__custom-btn', 'pswp__custom-btn--prev');
      prevBtn.innerHTML = `
        <svg viewBox="0 0 24 24" fill="white">
          <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
        </svg>
      `;
    }

    // 查找并自定义关闭按钮
    const closeBtn = pswpElement.querySelector('.pswp__button--close');
    if (closeBtn) {
      closeBtn.classList.add('pswp__custom-btn', 'pswp__custom-btn--close');
      closeBtn.innerHTML = `
        <svg viewBox="0 0 24 24" fill="white">
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
        </svg>
      `;
    }

    // 查找并自定义下一张按钮
    const nextBtn = pswpElement.querySelector('.pswp__button--arrow--next');
    if (nextBtn) {
      nextBtn.classList.add('pswp__custom-btn', 'pswp__custom-btn--next');
      nextBtn.innerHTML = `
        <svg viewBox="0 0 24 24" fill="white">
          <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
        </svg>
      `;
    }
  }

  /**
   * 手动绑定点击事件到所有slides
   */
  bindClickEvents() {
    const slides = this.closest('media-gallery').querySelectorAll('slideshow-slide');

    slides.forEach((slide, index) => {
      // 移除原有的事件监听器（如果有的话）
      slide.removeEventListener('click', slide._photoswipeClickHandler);

      // 创建新的事件处理器
      slide._photoswipeClickHandler = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.openPhotoSwipe(index);
      };

      // 绑定点击事件
      slide.addEventListener('click', slide._photoswipeClickHandler);
    });
  }
}

// 注册自定义元素
if (!customElements.get('photoswipe-gallery')) {
  customElements.define('photoswipe-gallery', PhotoSwipeGallery);
}

// 导出类
export default PhotoSwipeGallery;
